<template>
  <div class="flex flex-col gap-7 px-6">
    <div class="flex flex-col gap-2">
      <BaseText
        type="label"
        size="lg"
        class="text-text-loud"
      >
        Your connected ad accounts
      </BaseText>
      <BaseText
        type="body"
        size="md"
        class="text-text-normal max-w-3xl"
      >
        These Meta ad accounts are linked to {{ lens.name || 'your brand' }}. Spend on these accounts is eligible for ad credits once your pixel is live.
      </BaseText>
    </div>

    <div class="flex items-center justify-between gap-4 p-3 rounded-xl bg-white border border-border-normal">
      <div class="flex items-center gap-2.5">
        <div
          v-if="checkingStatus"
          class="skeleton w-2.5 h-2.5 rounded-full flex-shrink-0"
        />
        <div
          v-else
          class="w-2.5 h-2.5 rounded-full flex-shrink-0"
          :class="isConnected ? 'bg-secondary-green-300' : 'bg-neutral-300'"
        />
        <div class="flex flex-col">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            {{ connectionTitle }}
          </BaseText>
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued"
          >
            {{ connectionDescription }}
          </BaseText>
        </div>
      </div>

      <button
        v-if="!isConnected || connecting"
        class="black-btn-primary flex items-center gap-1 px-2.5 py-1.5 rounded-md flex-shrink-0"
        :disabled="connecting || checkingStatus || selectedAccountIds.length === 0"
        @click="handleConnect"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-white whitespace-nowrap"
        >
          {{ connecting ? 'Connecting...' : 'Connect' }}
        </BaseText>
        <BaseLoadingSpinnerCircle
          v-if="connecting"
          class="text-white w-4 h-4"
          :duration="1"
        />
      </button>
      <div
        v-else
        class="flex items-center gap-1 px-2 py-1 rounded-md bg-primary-green-10 flex-shrink-0"
      >
        <CheckmarkIcon
          :width="14"
          :height="14"
          class-name="text-secondary-green-300"
        />
        <BaseText
          type="label"
          size="xs"
          class="text-secondary-green-300 whitespace-nowrap"
        >
          Connected
        </BaseText>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-if="!loading && !accounts.length"
        class="flex items-center p-3 rounded-xl bg-white border border-border-normal"
      >
        <BaseText
          type="body"
          size="sm"
          class="text-text-subdued"
        >
          No Meta ad accounts are connected to this Lens. Connect a Meta ad account in your Lens settings to continue.
        </BaseText>
      </div>
      <template v-else-if="loading">
        <div
          v-for="n in 3"
          :key="`skeleton-account-${n}`"
          class="flex items-center h-11 p-1.5 rounded-xl bg-white border border-border-normal"
        >
          <div class="w-8 h-8 p-1.5 flex-shrink-0">
            <div class="skeleton w-5 h-5 rounded-md" />
          </div>
          <div class="flex flex-1 min-w-0 items-center justify-between gap-2 p-1.5">
            <div
              class="skeleton h-4 rounded-md"
              :style="{ width: `${120 + n * 30}px` }"
            />
            <div class="skeleton h-4 w-20 rounded-md flex-shrink-0" />
          </div>
        </div>
      </template>

      <div
        v-for="account in accounts"
        v-else
        :key="account.ad_account_id"
        class="flex items-center p-1.5 rounded-xl bg-white border border-border-normal"
        :class="isLocked(account.ad_account_id) ? 'cursor-default' : 'cursor-pointer'"
        @click="toggleAccount(account.ad_account_id)"
      >
        <div class="flex items-center p-1 flex-shrink-0">
          <div
            class="w-3.5 h-3.5 rounded flex items-center justify-center transition-colors"
            :class="[
              isSelected(account.ad_account_id) ? 'bg-black bg-opacity-80' : 'bg-white',
              { 'opacity-40': isSubmitted(account.ad_account_id) }
            ]"
            :style="!isSelected(account.ad_account_id)
              ? 'box-shadow: 0px 0px 0px 1px rgba(9,25,72,0.13), 0px 1px 2px 0px rgba(9,25,72,0.13)'
              : ''"
          >
            <CheckmarkIcon
              v-if="isSelected(account.ad_account_id)"
              :width="10"
              :height="10"
              class-name="text-white"
            />
          </div>
        </div>

        <div class="flex items-center justify-center p-1.5 rounded-md flex-shrink-0 w-8 h-8">
          <component
            :is="getPlatformIcon(account.provider)"
            :width="20"
            :height="20"
          />
        </div>

        <div class="flex flex-1 min-w-0 items-center gap-2.5 p-1.5 rounded-md overflow-hidden">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted whitespace-nowrap flex-shrink-0"
          >
            {{ account.ad_account_name }}
          </BaseText>
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued whitespace-nowrap flex-shrink-0"
          >
            {{ account.ad_account_id }}
          </BaseText>
        </div>

        <div
          v-if="isConnectingAccount(account.ad_account_id)"
          class="flex items-center gap-1 px-1.5 flex-shrink-0"
        >
          <BaseLoadingSpinnerCircle
            class="text-icon-normal w-3.5 h-3.5"
            :duration="1"
          />
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued whitespace-nowrap"
          >
            Connecting...
          </BaseText>
        </div>
        <div
          v-else-if="checkingStatus && !connections.length"
          class="skeleton h-3 w-16 rounded-md flex-shrink-0 mx-1.5"
        />
        <BaseText
          v-else-if="getAccountError(account.ad_account_id)"
          type="body"
          size="xs"
          class="text-primary-red-300 whitespace-nowrap flex-shrink-0 px-1.5"
          :title="getAccountError(account.ad_account_id)"
        >
          Failed
        </BaseText>
        <BaseText
          v-else-if="getAccountStatusLabel(account.ad_account_id)"
          type="body"
          size="xs"
          class="whitespace-nowrap flex-shrink-0 px-1.5"
          :class="getAccountStatus(account.ad_account_id) === 'connected' ? 'text-secondary-green-300' : 'text-text-subdued'"
        >
          {{ getAccountStatusLabel(account.ad_account_id) }}
        </BaseText>
      </div>
    </div>

    <div class="flex items-start">
      <button
        class="black-btn-primary flex items-center gap-1 pl-2.5 pr-1.5 py-1.5 rounded-md"
        :disabled="loading || submitting || connecting || checkingStatus || selectedAccountIds.length === 0 || !isConnected"
        @click="handleContinue"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-white whitespace-nowrap"
        >
          Continue to {{ nextStepLabel || 'Next' }}
        </BaseText>
        <BaseLoadingSpinnerCircle
          v-if="submitting"
          class="text-white w-5 h-5"
          :duration="1"
        />
        <ChevronIcon
          v-else
          class-name="text-white transform -rotate-90"
          :width="20"
          :height="20"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { getPlatformIcon } from '@/utils/lens/dataSourceUtils'
import LensAPI from '../../../../api/lens/LensRequests'
import BaseText from '../../../globals/BaseText.vue'
import CheckmarkIcon from '../../../globals/Icons/CheckmarkIcon.vue'
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'
import KebabMenuIcon from '../../../globals/Icons/KebabMenuIcon.vue'

const normalizeAccountId = id => String(id).replace(/^act_/, '')

// Spendback moves accounts from pending_activation to connected on its own, so both count as done
const SUBMITTED_STATUSES = ['connected', 'pending_activation']

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback

export default {
  name: 'SpendBackStepAccounts',
  components: {
    BaseText,
    CheckmarkIcon,
    ChevronIcon,
    KebabMenuIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    },
    accounts: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    nextStepLabel: {
      type: String,
      default: null
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedAccountIds: [],
      platform: 'meta',
      connections: [],
      // Last connect error per ad account, keyed by numeric ad account id
      connectErrors: {},
      // Ad account ids in the current connect request
      connectingIds: [],
      connecting: false,
      checkingStatus: false
    }
  },
  computed: {
    // Spendback connection status keyed by numeric ad account id
    connectionStatusById () {
      return this.connections.reduce((acc, connection) => {
        if (connection?.platform === 'meta_ads' && connection.account_id) {
          acc[normalizeAccountId(connection.account_id)] = connection.status
        }
        return acc
      }, {})
    },
    connectionTitle () {
      if (this.connecting) return 'Connecting your ad accounts...'
      if (this.checkingStatus && !this.connections.length) return 'Checking connection status...'
      return this.isConnected ? 'Ad accounts connected' : 'Connect your ad accounts'
    },
    connectionDescription () {
      if (this.connecting) return 'This can take a few seconds per ad account.'
      if (this.isConnected) return 'You are all set to continue.'
      return 'Authorize access so we can request ad credits on your behalf.'
    },
    isConnected () {
      return this.selectedAccountIds.length > 0 &&
        this.selectedAccountIds.every(id => this.isSubmitted(id))
    }
  },
  watch: {
    accounts: {
      immediate: true,
      handler (val) {
        if (val?.length && !this.selectedAccountIds.length) {
          this.selectedAccountIds = val.map(a => a.ad_account_id)
        }
      }
    }
  },
  async mounted () {
    await this.checkConnectStatus()
  },
  methods: {
    getPlatformIcon (platform) {
      return getPlatformIcon(platform)
    },
    getAccountStatus (id) {
      return this.connectionStatusById[normalizeAccountId(id)] || null
    },
    getAccountStatusLabel (id) {
      const labels = {
        connected: 'Connected',
        pending_activation: 'Pending',
        needs_reconnect: 'Needs reconnect',
        disconnected: 'Disconnected'
      }
      return labels[this.getAccountStatus(id)] || null
    },
    isSubmitted (id) {
      return SUBMITTED_STATUSES.includes(this.getAccountStatus(id))
    },
    isConnectingAccount (id) {
      return this.connectingIds.includes(id)
    },
    isLocked (id) {
      return this.connecting || this.isSubmitted(id)
    },
    getAccountError (id) {
      if (this.isSubmitted(id)) return null
      return this.connectErrors[normalizeAccountId(id)] || null
    },
    async checkConnectStatus () {
      this.checkingStatus = true
      try {
        const { data } = await LensAPI.SpendBack.getConnectStatus(this.lensId, 'meta_ads')
        const connections = Array.isArray(data) ? data : data?.data
        this.connections = Array.isArray(connections) ? connections : []

        // Accounts already submitted to Spendback stay selected and locked
        this.accounts.forEach(account => {
          if (this.isSubmitted(account.ad_account_id) && !this.isSelected(account.ad_account_id)) {
            this.selectedAccountIds.push(account.ad_account_id)
          }
        })
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: getErrorMessage(error, 'Could not load your ad account connection status. Please refresh and try again.')
        })
      } finally {
        this.checkingStatus = false
      }
    },
    async handleConnect () {
      if (this.connecting) return

      // Re-submitting needs_reconnect / disconnected accounts refreshes their token
      const accountIds = this.selectedAccountIds.filter(id => !this.isSubmitted(id))
      if (!accountIds.length) return

      this.connecting = true
      this.connectingIds = accountIds
      this.connectErrors = {}
      try {
        const { data } = await LensAPI.SpendBack.connect(this.lensId, accountIds)
        const failed = data?.failed || []

        if (failed.length) {
          this.connectErrors = failed.reduce((acc, result) => {
            acc[normalizeAccountId(result.account_id)] = result.message || 'Failed to connect this ad account'
            return acc
          }, {})

          const firstReason = failed[0].message
          this.$showAlert({
            type: 'error',
            message: failed.length === accountIds.length
              ? firstReason || 'Failed to connect your ad accounts. Please try again.'
              : `${failed.length} of ${accountIds.length} ad accounts failed to connect. Hover "Failed" on an account to see why.`
          })
        } else {
          this.$showAlert({ type: 'success', message: 'Ad accounts connected' })
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: getErrorMessage(error, 'Failed to connect your ad accounts. Please try again.')
        })
      }

      // Stay in the connecting state until the refreshed statuses are in, so rows don't flash back
      await this.checkConnectStatus()
      this.connectingIds = []
      this.connecting = false
    },
    isSelected (id) {
      return this.selectedAccountIds.includes(id)
    },
    toggleAccount (id) {
      if (this.isLocked(id)) return

      const idx = this.selectedAccountIds.indexOf(id)
      if (idx > -1) {
        this.selectedAccountIds.splice(idx, 1)
      } else {
        this.selectedAccountIds.push(id)
      }
    },
    handleContinue () {
      this.$emit('continue', { selectedAccountIds: [...this.selectedAccountIds] })
    }
  }
}
</script>
